import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Search, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";

const FILTERS = [
  { key: "ALL", label: "All" },
  { key: "COMPLETED", label: "Completed" },
  { key: "FAILED", label: "Failed" },
];

const formatDateGroup = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(d, now)) return "Today";
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

export const ActivityScreen = () => {
  const nav = useNavigate();
  const { transactions, partner } = useApp();
  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return transactions.filter((tx) => {
      if (filter !== "ALL" && tx.status !== filter) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        return (
          tx.recipient?.name?.toLowerCase().includes(q) ||
          tx.reference?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [transactions, filter, query]);

  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach((tx) => {
      const key = formatDateGroup(tx.createdAt);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(tx);
    });
    return Array.from(m.entries());
  }, [filtered]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full flex flex-col"
      data-testid="activity-screen"
    >
      <div className="px-6 pt-5 pb-3">
        <h1 className="font-display text-[28px] font-bold tracking-tight dark:text-white">Activity</h1>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 mt-1">
          <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
          Every transfer is executed by <b className="text-gray-700 dark:text-gray-300">{partner.name}</b>
        </div>
      </div>

      <div className="px-6 pb-3">
        <div className="relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            strokeWidth={2.5}
          />
          <input
            data-testid="activity-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or reference"
            className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-11 pl-11 pr-5 font-medium outline-none text-sm dark:text-white dark:placeholder-gray-500"
          />
        </div>
      </div>

      <div className="px-6 pb-3 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`activity-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={
              "h-8 px-4 rounded-full text-[12px] font-semibold transition-all " +
              (filter === f.key
                ? "bg-gray-900 dark:bg-emerald-600 text-white"
                : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto phone-scroll px-6 pb-32">
        {grouped.length === 0 && (
          <div className="text-center py-12">
            <div className="text-sm font-semibold text-gray-700 dark:text-white">No transactions</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Nothing matches your filters.
            </div>
          </div>
        )}
        {grouped.map(([group, items]) => (
          <div key={group} className="mb-4">
            <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
              {group}
            </div>
            <div className="space-y-1">
              {items.map((tx) => (
                <button
                  key={tx.id}
                  data-testid={`activity-tx-${tx.id}`}
                  onClick={() => nav(`/transaction/${tx.id}`)}
                  className="w-full flex items-center gap-3 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 rounded-2xl px-2 -mx-2 transition-colors text-left"
                >
                  <Avatar
                    initials={tx.recipient?.initials}
                    color={tx.recipient?.color}
                    size={40}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-[14px] text-gray-900 dark:text-white truncate">
                        {tx.recipient?.name}
                      </div>
                      {tx.status === "FAILED" && (
                        <span className="text-[9px] font-bold text-red-600 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                          FAILED
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      Executed by {tx.partner} · {tx.reference}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-gray-900 dark:text-white font-semibold text-[14px]">
                    <ArrowUpRight className="w-3.5 h-3.5 text-gray-400" strokeWidth={2.5} />
                    {tx.amount.toFixed(2)} kr
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default ActivityScreen;
